import { Box, Tooltip, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useIntl } from "react-intl";
import Messages from "../../../../../localization/Messages";
import ChartComponent from "../../ChartComponent/ChartComponent";
import { ExpenseStatsDock } from "../../../../../models/stats";
import { formatNumber } from "../../../../../helpers/numberDisplayHelper";

type FeesExpensesAnalyticsProps = {
    expenseStatsDock: ExpenseStatsDock[];
};

export const FeesExpensesAnalytics = ({ expenseStatsDock }: FeesExpensesAnalyticsProps) => {
    const intl = useIntl();

    const dataAvailable = expenseStatsDock.length > 0;

    const totals = dataAvailable
        ? expenseStatsDock.reduce(
              (acc, dock) => {
                  acc.totalAmmountInvoiced += dock.totalCost;
                  acc.totalNumberOfInvoices += dock.countInvoices;
                  acc.totalBerthingCosts += dock.arrivalCost + dock.layCost;
                  acc.totalWasteCost += dock.wasteCost;
                  acc.totalServiceFeeCost += dock.serviceCost;
                  acc.totalWayFeeCost += dock.wayFeeCost;
                  acc.totalWaterCost += dock.waterCost;
                  acc.totalElectricityCost += dock.electricityCost;
                  return acc;
              },
              {
                  totalAmmountInvoiced: 0,
                  totalNumberOfInvoices: 0,
                  totalBerthingCosts: 0,
                  totalWasteCost: 0,
                  totalServiceFeeCost: 0,
                  totalWayFeeCost: 0,
                  totalWaterCost: 0,
                  totalElectricityCost: 0,
              },
          )
        : {
              totalAmmountInvoiced: 0,
              totalNumberOfInvoices: 0,
              totalBerthingCosts: 0,
              totalWasteCost: 0,
              totalServiceFeeCost: 0,
              totalWayFeeCost: 0,
              totalWaterCost: 0,
              totalElectricityCost: 0,
          };

    const {
        totalAmmountInvoiced,
        totalNumberOfInvoices,
        totalBerthingCosts,
        totalWasteCost,
        totalServiceFeeCost,
        totalWayFeeCost,
        totalWaterCost,
        totalElectricityCost,
    } = totals;

    return (
        <Box className="exoenses-and-fees-container shadow" my={2} mx={2.5} padding={2.5}>
            <Typography variant="h5">
                {intl.formatMessage(Messages.calculationFeesAndExpenses)}
                <Tooltip title={intl.formatMessage(Messages.onlyInvoicedConfirmedChangedTooltip)}>
                    <HelpOutlineIcon fontSize="small" color="action" />
                </Tooltip>
            </Typography>
            <ChartComponent
                data={
                    dataAvailable
                        ? [
                              {
                                  label: intl.formatMessage(Messages.berthingFees),
                                  value: totalBerthingCosts,
                              },
                              {
                                  label: intl.formatMessage(Messages.garbageFees),
                                  value: totalWasteCost,
                              },
                              {
                                  label: intl.formatMessage(Messages.serviceFees),
                                  value: totalServiceFeeCost,
                              },
                              {
                                  label: intl.formatMessage(Messages.wayFees),
                                  value: totalWayFeeCost,
                              },
                              {
                                  label: intl.formatMessage(Messages.waterConsumption),
                                  value: totalWaterCost,
                              },
                              {
                                  label: intl.formatMessage(Messages.electricity),
                                  value: totalElectricityCost,
                              },
                          ]
                        : []
                }
                description={intl.formatMessage(Messages.ammountByType)}
                outerStats={[
                    `${intl.formatMessage(Messages.totalAmountInvoiced)} = CHF ${formatNumber(
                        totalAmmountInvoiced,
                    )}`,
                    `${intl.formatMessage(Messages.numberOfInvoices)} = ${totalNumberOfInvoices}`,
                ]}
                formatter={value =>
                    `${formatNumber(value)} / ${((value / totalAmmountInvoiced) * 100).toFixed(2)}%`
                }
            />
        </Box>
    );
};
